import { lazy } from 'react';

import type { RouteConfig } from '../../types';

// Lazy load layouts
const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'));
const CompaniesLayout = lazy(() => import('@/pages/companies/layout'));
const CompanyLayout = lazy(() => import('@/pages/companies/[companyId]/layout'));
const CompanyClientLayout = lazy(() => import('@/pages/companies/[companyId]/clients/[clientId]/layout'));
const JobSpecLayout = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/jobSpecifications/[jobSpecificationId]/layout')
);
const CompanyClientContractsLayout = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/contracts/layout')
);
const CompanyClientContractLayout = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/contracts/[contractId]/layout')
);
const CompanyClientCompanyContractsLayout = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/layout')
);
const CompanyClientCompanyContractLayout = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/[companyContractId]/layout')
);
const EmployeeTabsLayout = lazy(() => import('@/pages/companies/[companyId]/employees/[userId]/(tabs)/layout'));

// Lazy load company pages
const CompaniesPage = lazy(() => import('@/pages/companies'));
const CreateCompanyPage = lazy(() => import('@/pages/companies/create'));
const CompanyInfoPage = lazy(() => import('@/pages/companies/[companyId]/info'));
const UpdateCompanyPage = lazy(() => import('@/pages/companies/[companyId]/update'));
const CompanyClientsPage = lazy(() => import('@/pages/companies/[companyId]/clients'));
const ConnectClientPage = lazy(() => import('@/pages/companies/[companyId]/clients/connect'));
const ViewJobSpecPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/jobSpecifications/[jobSpecificationId]/view')
);
const UpdateJobSpecPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/jobSpecifications/[jobSpecificationId]/update')
);
const CompanyClientContractsPage = lazy(() => import('@/pages/companies/[companyId]/clients/[clientId]/contracts'));
const CompanyClientContractJobSpecsPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/contracts/[contractId]/jobSpecifications')
);
const CreateCompanyClientContractJobSpecPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/contracts/[contractId]/jobSpecifications/create')
);
const CompanyClientCompanyContractsPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts')
);
const CreateCompanyClientCompanyContractPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/create')
);
const CompanyClientCompanyContractJobSpecsPage = lazy(
  () =>
    import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/[companyContractId]/jobSpecifications')
);
const UpdateCompanyClientCompanyContractPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/[companyContractId]/update')
);
const ViewCompanyClientCompanyContractPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/[companyContractId]/view')
);
const CompanyClientCompanyContractHistoryPage = lazy(
  () => import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/[companyContractId]/history')
);
const CreateCompanyClientCompanyContractJobSpecPage = lazy(
  () =>
    import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/[companyContractId]/jobSpecifications/create')
);
const ManageCompanyClientCompanyContractJobSpecPricesPage = lazy(
  () =>
    import('@/pages/companies/[companyId]/clients/[clientId]/companyContracts/[companyContractId]/jobSpecifications/managePrices')
);
const CompanyEmployeesPage = lazy(() => import('@/pages/companies/[companyId]/employees'));
const CreateEmployeePage = lazy(() => import('@/pages/companies/[companyId]/employees/create'));
const EmployeeDetailsPage = lazy(() => import('@/pages/companies/[companyId]/employees/[userId]/(tabs)/details'));
const EditEmployeePage = lazy(() => import('@/pages/companies/[companyId]/employees/[userId]/(tabs)/edit'));
const EmployeePermissionsPage = lazy(
  () => import('@/pages/companies/[companyId]/employees/[userId]/(tabs)/permissions')
);
const EmployeeHistoryPage = lazy(() => import('@/pages/companies/[companyId]/employees/[userId]/(tabs)/history'));
const CompanyCoiLayout = lazy(() => import('@/pages/companies/[companyId]/coi/layout'));
const CompanyCoiPage = lazy(() => import('@/pages/companies/[companyId]/coi'));
const CompanyCoiCommentsPage = lazy(() => import('@/pages/companies/[companyId]/coi/comments'));
const CompanyCoiHistoryPage = lazy(() => import('@/pages/companies/[companyId]/coi/history'));
const CreateCompanyCoiPage = lazy(() => import('@/pages/companies/[companyId]/coi/create'));
const UpdateCompanyCoiPage = lazy(() => import('@/pages/companies/[companyId]/coi/update'));

/**
 * Company routes
 * Handles company management, company clients, job specifications, and employees
 * This is one of the most complex route groups with deep nesting
 */
export const companyRoutes: RouteConfig = {
  path: '/companies',
  layout: DashboardLayout,
  children: [
    {
      path: '',
      layout: CompaniesLayout,
      children: [
        {
          path: '',
          element: CompaniesPage,
        },
        {
          path: '/create',
          element: CreateCompanyPage,
        },
      ],
    },
    {
      path: '/:companyId',
      layout: CompanyLayout,
      children: [
        {
          path: '/info',
          element: CompanyInfoPage,
        },
        {
          path: '/update',
          element: UpdateCompanyPage,
        },
        {
          path: '/clients',
          element: CompanyClientsPage,
        },
        {
          path: '/clients/connect',
          element: ConnectClientPage,
        },
        {
          path: '/clients/:clientId',
          layout: CompanyClientLayout,
          children: [
            {
              path: '/jobSpecifications/:jobSpecificationId',
              layout: JobSpecLayout,
              children: [
                {
                  path: '/view',
                  element: ViewJobSpecPage,
                },
                {
                  path: '/update',
                  element: UpdateJobSpecPage,
                },
              ],
            },
            {
              path: '/contracts',
              layout: CompanyClientContractsLayout,
              children: [
                {
                  path: '',
                  element: CompanyClientContractsPage,
                },
              ],
            },
            {
              path: '/contracts/:contractId',
              layout: CompanyClientContractLayout,
              children: [
                {
                  path: '/jobSpecifications',
                  element: CompanyClientContractJobSpecsPage,
                },
                {
                  path: '/jobSpecifications/create',
                  element: CreateCompanyClientContractJobSpecPage,
                },
              ],
            },
            {
              path: '/companyContracts',
              layout: CompanyClientCompanyContractsLayout,
              children: [
                {
                  path: '',
                  element: CompanyClientCompanyContractsPage,
                },
                {
                  path: '/create',
                  element: CreateCompanyClientCompanyContractPage,
                },
              ],
            },
            {
              path: '/companyContracts/:companyContractId',
              layout: CompanyClientCompanyContractLayout,
              children: [
                {
                  path: '/view',
                  element: ViewCompanyClientCompanyContractPage,
                },
                {
                  path: '/jobSpecifications',
                  element: CompanyClientCompanyContractJobSpecsPage,
                },
                {
                  path: '/jobSpecifications/create',
                  element: CreateCompanyClientCompanyContractJobSpecPage,
                },
                {
                  path: '/jobSpecifications/managePrices',
                  element: ManageCompanyClientCompanyContractJobSpecPricesPage,
                },
                {
                  path: '/update',
                  element: UpdateCompanyClientCompanyContractPage,
                },
                {
                  path: '/history',
                  element: CompanyClientCompanyContractHistoryPage,
                },
              ],
            },
          ],
        },
        {
          path: '/coi',
          layout: CompanyCoiLayout,
          children: [
            {
              path: '',
              element: CompanyCoiPage,
            },
            {
              path: '/comments',
              element: CompanyCoiCommentsPage,
            },
            {
              path: '/history',
              element: CompanyCoiHistoryPage,
            },
            {
              path: '/create',
              element: CreateCompanyCoiPage,
            },
            {
              path: '/update',
              element: UpdateCompanyCoiPage,
            },
          ],
        },
        {
          path: '/employees',
          element: CompanyEmployeesPage,
        },
        {
          path: '/employees/create',
          element: CreateEmployeePage,
        },
        {
          path: '/employees/:userId',
          layout: EmployeeTabsLayout,
          children: [
            {
              path: '/details',
              element: EmployeeDetailsPage,
            },
            {
              path: '/edit',
              element: EditEmployeePage,
            },
            {
              path: '/permissions',
              element: EmployeePermissionsPage,
            },
            {
              path: '/history',
              element: EmployeeHistoryPage,
            },
          ],
        },
      ],
    },
  ],
};
